/**
 * Sociedade (só sócios): painel, quadro de ações (colunas personalizáveis;
 * "Pauta" alimenta a próxima reunião) e reuniões com pauta, ata,
 * deliberações e PDF no papel timbrado.
 */
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useSearch, useTitle } from "@/lib/router";
import { fmtDate, isOverdue, todayIso } from "@/lib/format";
import { useLookups } from "@/lib/catalog";
import { Badge, Card, ErrorBox, Field, FormGrid, Loading, Modal, PageHeader, Stat, Tabs, UserSelect, useAct } from "@/ui/kit";
import { confirmar, toast } from "@/ui/feedback";
import { Avatar, Icon } from "@/ui/icons";
import { FinancePanel } from "./FinancePanel";

type Tab = "painel" | "quadro" | "reunioes" | "financeiro";
interface Escopo { all: boolean; units: Array<{ id: string; name: string }>; manageAll: boolean; manageUnits: string[]; canManage: boolean }
interface Col { id: string; title: string; color: string; border: string; position: number; isFinal: boolean; isAgenda: boolean }
interface Act {
  id: string; number: number; meetingId: string | null; columnId: string; position: number; title: string; description: string;
  responsibleUserId: string | null; responsibleName: string | null; dueDate: string | null; tags: string[]; progress: number; completedAt: string | null;
  unitIds: string[]; readOnly?: boolean;
}
interface Meeting { id: string; number: number; title: string; date: string; status: "agendada" | "realizada"; participants: string[]; agenda: string; minutes: string; decisions: string; unitIds: string[]; readOnly?: boolean }

function useEscopo() {
  return useQuery({ queryKey: ["soc-escopo"], queryFn: () => api.get<Escopo>("/sociedade/escopo"), staleTime: 60_000 });
}
/** Filtro de unidade da página (?unidade=). */
function useUnitFilter(): string { const [search] = useSearch(); return search.get("unidade") ?? ""; }

/** Etiquetas das unidades de um item ("Escritório todo" quando vazio). */
function UnitTags({ ids }: { ids: string[] }): JSX.Element {
  const { unitName } = useLookups();
  if (!ids.length) return <span className="tag tag-gray">Escritório todo</span>;
  return <>{ids.map((u) => <span key={u} className="tag tag-blue">{unitName(u)}</span>)}</>;
}

/** Escolha das unidades de um item, limitada às que a pessoa gerencia. */
function UnitsPicker({ value, onChange, disabled }: { value: string[]; onChange: (v: string[]) => void; disabled?: boolean }): JSX.Element {
  const { data: esc } = useEscopo();
  const { unitName } = useLookups();
  if (!esc) return <span className="muted small">…</span>;
  const opts = esc.manageAll ? esc.units.map((u) => u.id) : esc.manageUnits;
  return (
    <div className="chips">
      {esc.manageAll && <button type="button" disabled={disabled} className={`chip ${!value.length ? "on" : ""}`} onClick={() => onChange([])}>Escritório todo</button>}
      {opts.map((u) => (
        <button key={u} type="button" disabled={disabled} className={`chip ${value.includes(u) ? "on" : ""}`}
          onClick={() => onChange(value.includes(u) ? value.filter((x) => x !== u) : [...value, u])}>{unitName(u)}</button>
      ))}
    </div>
  );
}

const inv = [["soc-acoes"], ["soc-resumo"], ["soc-reunioes"], ["soc-reuniao"]];
const unitQs = (u: string) => (u ? `?unidade=${u}` : "");

export function SocietyPage(): JSX.Element {
  useTitle("Sociedade");
  const [search, set] = useSearch();
  const tab = (search.get("aba") as Tab) || "painel";
  const { can } = useLookups();
  const { data: esc } = useEscopo();
  const unit = useUnitFilter();
  const showUnitFilter = !!esc && (esc.all || esc.units.length > 1);
  return (
    <div className="stack gap-16">
      <PageHeader title="Sociedade" subtitle={esc && !esc.all ? `Suas unidades: ${esc.units.map((u) => u.name).join(", ") || "nenhuma"}` : "Reuniões de sócios, pauta, atas, ações e financeiro"}
        actions={showUnitFilter && (
          <select className="input" style={{ width: "auto" }} value={unit} onChange={(e) => set({ unidade: e.target.value || null, reuniao: null })}>
            <option value="">{esc!.all ? "Todas as unidades (inclui escritório todo)" : "Todas as minhas unidades"}</option>
            {esc!.units.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        )} />
      <Tabs<Tab> value={tab} onChange={(t) => set({ aba: t, reuniao: null })} tabs={[
        { key: "painel", label: "Painel" }, { key: "quadro", label: "Quadro de ações" }, { key: "reunioes", label: "Reuniões e atas" },
        { key: "financeiro", label: "Financeiro", hidden: !can("financeiro.gerente") },
      ]} />
      {tab === "painel" && <Summary />}
      {tab === "quadro" && <Board />}
      {tab === "reunioes" && <Meetings />}
      {tab === "financeiro" && <FinancePanel unitId={unit} />}
    </div>
  );
}

function useWho() {
  const { userName } = useLookups();
  return (a: { responsibleUserId: string | null; responsibleName: string | null }) => (a.responsibleUserId ? userName(a.responsibleUserId) : a.responsibleName || "—");
}

// =================================================================== painel

function Summary(): JSX.Element {
  const [, set] = useSearch();
  const who = useWho();
  const { userName } = useLookups();
  const unit = useUnitFilter();
  const { data, error } = useQuery({
    queryKey: ["soc-resumo", unit],
    queryFn: () => api.get<{ open: number; overdue: number; agenda: number; byResponsible: Array<{ key: string; count: number }>; upcoming: Act[]; nextMeeting: Meeting | null; lastMeetings: Array<{ id: string; number: number; title: string; date: string }> }>(`/sociedade/resumo${unitQs(unit)}`),
  });
  if (error) return <ErrorBox error={error} />;
  if (!data) return <Loading />;
  return (
    <div className="stack gap-16">
      <div className="grid cols-4">
        <Stat label="Ações em aberto" value={data.open} onClick={() => set({ aba: "quadro" })} />
        <Stat label="Atrasadas" value={data.overdue} tone={data.overdue ? "bad" : "ok"} />
        <Stat label="Na pauta" value={data.agenda} hint="para a próxima reunião" />
        <Stat label="Próxima reunião" value={data.nextMeeting ? fmtDate(data.nextMeeting.date) : "—"} hint={data.nextMeeting?.title} onClick={data.nextMeeting ? () => set({ aba: "reunioes", reuniao: data.nextMeeting!.id }) : undefined} />
      </div>
      <div className="grid side">
        <Card title="Próximos prazos">
          {data.upcoming.length ? data.upcoming.map((a) => (
            <div key={a.id} className="lrow" style={{ cursor: "default" }}>
              <div className="grow"><div className="tt">#{a.number} {a.title}</div><div className="mm">{who(a)}</div></div>
              <Badge tone={a.dueDate && isOverdue(a.dueDate) ? "bad" : "info"}>{fmtDate(a.dueDate)}</Badge>
            </div>
          )) : <div className="muted small">Nenhuma ação com prazo.</div>}
        </Card>
        <div className="stack gap-16">
          <Card title="Em aberto por responsável">
            {data.byResponsible.map((r) => (
              <div key={r.key || "none"} className="row between"><span>{r.key ? (r.key.length === 36 ? userName(r.key) : r.key) : "Sem responsável"}</span><strong>{r.count}</strong></div>
            ))}
            {!data.byResponsible.length && <div className="muted small">Nada em aberto.</div>}
          </Card>
          <Card title="Últimas atas">
            {data.lastMeetings.map((m) => (
              <div key={m.id} className="lrow" onClick={() => set({ aba: "reunioes", reuniao: m.id })}>
                <div className="grow"><div className="tt">Ata nº {m.number} — {m.title}</div><div className="mm">{fmtDate(m.date)}</div></div>
              </div>
            ))}
            {!data.lastMeetings.length && <div className="muted small">Nenhuma reunião realizada ainda.</div>}
          </Card>
        </div>
      </div>
    </div>
  );
}

// =================================================================== quadro

function Board({ meetingId, meetingUnits }: { meetingId?: string; meetingUnits?: string[] }): JSX.Element {
  const { can } = useLookups();
  const qc = useQueryClient();
  const who = useWho();
  const edit = can("sociedade.gerenciar");
  const { data: esc } = useEscopo();
  const colsAdmin = edit && !!esc?.manageAll;
  const unit = useUnitFilter();
  const cols = useQuery({ queryKey: ["soc-colunas"], queryFn: () => api.get<Col[]>("/sociedade/colunas") });
  const acts = useQuery({
    queryKey: ["soc-acoes", meetingId ?? "", meetingId ? "" : unit],
    queryFn: () => api.get<Act[]>(meetingId ? `/sociedade/acoes?meetingId=${meetingId}` : `/sociedade/acoes${unitQs(unit)}`),
  });
  const [dlg, setDlg] = useState<Act | { columnId: string } | null>(null);
  const [colDlg, setColDlg] = useState<Col | "new" | null>(null);
  const [drag, setDrag] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  if (cols.error || acts.error) return <ErrorBox error={cols.error ?? acts.error} />;
  if (!cols.data || !acts.data) return <Loading />;
  const f = filter.trim().toLowerCase();
  const list = acts.data.filter((a) => (!meetingId || a.meetingId === meetingId) && (!f || `${a.title} ${a.description} ${who(a)} ${a.tags.join(" ")}`.toLowerCase().includes(f)));

  const drop = async (columnId: string, position: number) => {
    if (!drag) return;
    const id = drag;
    setDrag(null);
    try {
      await api.post(`/sociedade/acoes/${id}/mover`, { columnId, position });
      await Promise.all(inv.map((k) => qc.invalidateQueries({ queryKey: k })));
    } catch (e) { toast.error((e as Error).message); }
  };
  const moveCol = async (c: Col, dir: -1 | 1) => {
    const ids = cols.data!.map((x) => x.id);
    const i = ids.indexOf(c.id), j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j]!, ids[i]!];
    await api.put("/sociedade/colunas/ordem", { ids });
    await qc.invalidateQueries({ queryKey: ["soc-colunas"] });
  };

  return (
    <div className="stack gap-12">
      <div className="row">
        <input className="input" style={{ maxWidth: 280 }} placeholder="Filtrar ações…" value={filter} onChange={(e) => setFilter(e.target.value)} />
        <span className="grow" />
        {colsAdmin && <button className="btn small" onClick={() => setColDlg("new")}>+ Coluna</button>}
        {edit && <button className="btn small primary" onClick={() => setDlg({ columnId: cols.data![0]?.id ?? "" })}>Nova ação</button>}
      </div>
      <div className="kanban">
        {cols.data.map((c) => {
          const items = list.filter((a) => a.columnId === c.id).sort((a, b) => a.position - b.position);
          return (
            <div key={c.id} className="kcol" style={{ background: c.color, borderTop: `3px solid ${c.border}` }}
              onDragOver={(e) => { if (edit) e.preventDefault(); }} onDrop={() => void drop(c.id, items.length)}>
              <div className="kcol-head">
                <b style={{ color: c.border }}>{c.title}</b> <span className="muted small">{items.length}</span>
                {c.isAgenda && <Badge tone="info">pauta</Badge>}{c.isFinal && <Badge tone="ok">final</Badge>}
                <span className="grow" />
                {colsAdmin && <><button className="link-btn small" onClick={() => void moveCol(c, -1)}>←</button><button className="link-btn small" onClick={() => void moveCol(c, 1)}>→</button><button className="link-btn small" onClick={() => setColDlg(c)}>editar</button></>}
              </div>
              {items.map((a, i) => (
                <div key={a.id} className={`kcard ${a.readOnly ? "ro" : ""}`} draggable={edit && !a.readOnly} onDragStart={() => setDrag(a.id)}
                  onDragOver={(e) => { if (edit) e.preventDefault(); }} onDrop={(e) => { e.stopPropagation(); void drop(c.id, i); }}
                  onClick={() => setDlg(a)}>
                  <div className="tt">#{a.number} {a.title}</div>
                  <div className="row small" style={{ gap: 6, marginTop: 4 }}>
                    {a.responsibleUserId ? <Avatar name={who(a)} id={a.responsibleUserId} size={18} /> : a.responsibleName ? <span className="muted">{a.responsibleName}</span> : null}
                    {a.dueDate && <Badge tone={!c.isFinal && isOverdue(a.dueDate) ? "bad" : "neutral"}>{fmtDate(a.dueDate)}</Badge>}
                    {a.progress > 0 && <span className="muted">{a.progress}%</span>}
                    {a.tags.map((t) => <Badge key={t} tone="muted">{t}</Badge>)}
                  </div>
                  <div className="row small" style={{ gap: 4, marginTop: 4 }}><UnitTags ids={a.unitIds} />{a.readOnly && <span className="muted">só leitura</span>}</div>
                </div>
              ))}
              {edit && <button className="link-btn small" onClick={() => setDlg({ columnId: c.id })}>+ ação</button>}
            </div>
          );
        })}
      </div>
      {dlg && <ActionModal act={"id" in dlg ? dlg : null} columnId={"id" in dlg ? dlg.columnId : dlg.columnId} meetingId={meetingId ?? null} meetingUnits={meetingUnits} cols={cols.data} onClose={() => setDlg(null)} />}
      {colDlg && <ColumnModal col={colDlg === "new" ? null : colDlg} cols={cols.data} onClose={() => setColDlg(null)} />}
    </div>
  );
}

function ActionModal({ act, columnId, meetingId, meetingUnits, cols, onClose }: { act: Act | null; columnId: string; meetingId: string | null; meetingUnits?: string[]; cols: Col[]; onClose: () => void }): JSX.Element {
  const { can } = useLookups();
  const { data: esc } = useEscopo();
  const unitFilter = useUnitFilter();
  const edit = can("sociedade.gerenciar") && !act?.readOnly;
  const defaultUnits = (): string[] => {
    const mine = esc ? (esc.manageAll ? esc.units.map((u) => u.id) : esc.manageUnits) : [];
    const base = meetingUnits?.length ? meetingUnits : unitFilter ? [unitFilter] : [];
    const ok = base.filter((u) => mine.includes(u));
    if (ok.length) return ok;
    return esc && !esc.manageAll && esc.manageUnits.length === 1 ? esc.manageUnits : [];
  };
  const [units, setUnits] = useState<string[]>(act?.unitIds ?? defaultUnits());
  const [f, setF] = useState({
    title: act?.title ?? "", description: act?.description ?? "", columnId: act?.columnId ?? columnId,
    responsibleUserId: act?.responsibleUserId ?? null as string | null, responsibleName: act?.responsibleName ?? "",
    dueDate: act?.dueDate?.slice(0, 10) ?? "", tags: act?.tags.join(", ") ?? "", progress: act?.progress ?? 0,
  });
  const body = () => ({
    ...f, responsibleName: f.responsibleUserId ? null : f.responsibleName || null, dueDate: f.dueDate || null,
    tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean), ...(act ? {} : { meetingId }), unitIds: units,
  });
  const needUnits = !!esc && !esc.manageAll && !units.length;
  const save = useAct(() => (act ? api.patch(`/sociedade/acoes/${act.id}`, body()) : api.post("/sociedade/acoes", body())), inv, onClose);
  const del = useAct(() => api.del(`/sociedade/acoes/${act!.id}`), inv, onClose, "Ação excluída");
  return (
    <Modal title={act ? `Ação #${act.number}` : "Nova ação"} onClose={onClose} footer={edit ? (
      <>
        {act && <button className="btn danger" style={{ marginRight: "auto" }} onClick={() => void confirmar("Excluir esta ação?", { danger: true }).then((ok) => ok && del.mutate())}>Excluir</button>}
        <button className="btn" onClick={onClose}>Cancelar</button>
        <button className="btn primary" disabled={f.title.trim().length < 2 || save.isPending || needUnits} onClick={() => save.mutate()}>Salvar</button>
      </>
    ) : <button className="btn" onClick={onClose}>Fechar</button>}>
      <ErrorBox error={save.error ?? del.error} />
      <FormGrid cols={2}>
        <Field label="O quê" required span={2}><input className="input" value={f.title} disabled={!edit} onChange={(e) => setF({ ...f, title: e.target.value })} /></Field>
        <Field label="Unidade(s)" required={!!esc && !esc.manageAll} span={2} hint={act?.readOnly ? "Ação de outra unidade — só leitura para você." : "Quem é sócio dessas unidades vê esta ação."}>{edit ? <UnitsPicker value={units} onChange={setUnits} /> : <div className="chips"><UnitTags ids={units} /></div>}</Field>
        <Field label="Detalhes" span={2}><textarea className="input" rows={4} value={f.description} disabled={!edit} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
        <Field label="Responsável (usuário)"><UserSelect value={f.responsibleUserId} onChange={(v) => setF({ ...f, responsibleUserId: v })} emptyLabel="Outra pessoa / ninguém" /></Field>
        {!f.responsibleUserId ? <Field label="…ou nome"><input className="input" value={f.responsibleName} disabled={!edit} onChange={(e) => setF({ ...f, responsibleName: e.target.value })} placeholder="Ex.: contador" /></Field> : <div />}
        <Field label="Prazo"><input type="date" className="input" value={f.dueDate} disabled={!edit} onChange={(e) => setF({ ...f, dueDate: e.target.value })} /></Field>
        <Field label="Coluna">
          <select className="input" value={f.columnId} disabled={!edit} onChange={(e) => setF({ ...f, columnId: e.target.value })}>{cols.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}</select>
        </Field>
        <Field label="Etiquetas" hint="Separe por vírgula: financeiro, rh, comercial…"><input className="input" value={f.tags} disabled={!edit} onChange={(e) => setF({ ...f, tags: e.target.value })} /></Field>
        <Field label={`Andamento: ${f.progress}%`}><input type="range" min={0} max={100} step={10} value={f.progress} disabled={!edit} onChange={(e) => setF({ ...f, progress: Number(e.target.value) })} /></Field>
      </FormGrid>
    </Modal>
  );
}

function ColumnModal({ col, cols, onClose }: { col: Col | null; cols: Col[]; onClose: () => void }): JSX.Element {
  const [f, setF] = useState({ title: col?.title ?? "", color: col?.color ?? "#eef2ff", border: col?.border ?? "#4f5d9a", isFinal: col?.isFinal ?? false, isAgenda: col?.isAgenda ?? false });
  const [dest, setDest] = useState("");
  const i = [["soc-colunas"], ...inv];
  const save = useAct(() => (col ? api.patch(`/sociedade/colunas/${col.id}`, f) : api.post("/sociedade/colunas", f)), i, onClose);
  const del = useAct(() => api.del(`/sociedade/colunas/${col!.id}${dest ? `?destino=${dest}` : ""}`), i, onClose, "Coluna removida");
  return (
    <Modal title={col ? `Coluna — ${col.title}` : "Nova coluna"} onClose={onClose} footer={
      <>
        {col && <button className="btn danger" style={{ marginRight: "auto" }} onClick={() => void confirmar("Remover a coluna? As ações vão para a coluna escolhida.", { danger: true }).then((ok) => ok && del.mutate())}>Remover</button>}
        <button className="btn" onClick={onClose}>Cancelar</button>
        <button className="btn primary" disabled={!f.title.trim() || save.isPending} onClick={() => save.mutate()}>Salvar</button>
      </>
    }>
      <ErrorBox error={save.error ?? del.error} />
      <FormGrid cols={2}>
        <Field label="Título" span={2}><input className="input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></Field>
        <Field label="Cor de fundo"><input type="color" value={f.color} onChange={(e) => setF({ ...f, color: e.target.value })} /></Field>
        <Field label="Cor de destaque"><input type="color" value={f.border} onChange={(e) => setF({ ...f, border: e.target.value })} /></Field>
        <label className="row nowrap span-2"><input type="checkbox" checked={f.isAgenda} onChange={(e) => setF({ ...f, isAgenda: e.target.checked })} /> É a coluna de <b>pauta</b> (entra na próxima reunião)</label>
        <label className="row nowrap span-2"><input type="checkbox" checked={f.isFinal} onChange={(e) => setF({ ...f, isFinal: e.target.checked })} /> Coluna final (ação concluída ao cair aqui)</label>
        {col && <Field label="Ao remover, mover as ações para" span={2}>
          <select className="input" value={dest} onChange={(e) => setDest(e.target.value)}><option value="">Primeira coluna</option>{cols.filter((c) => c.id !== col.id).map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}</select>
        </Field>}
      </FormGrid>
    </Modal>
  );
}

// ================================================================= reuniões

function Meetings(): JSX.Element {
  const [search, set] = useSearch();
  const { can } = useLookups();
  const openId = search.get("reuniao");
  const unit = useUnitFilter();
  const { data: esc } = useEscopo();
  const { data, error } = useQuery({ queryKey: ["soc-reunioes", unit], queryFn: () => api.get<Meeting[]>(`/sociedade/reunioes${unitQs(unit)}`) });
  const newUnits = (): string[] => {
    if (!esc) return [];
    const mine = esc.manageAll ? esc.units.map((u) => u.id) : esc.manageUnits;
    if (unit && mine.includes(unit)) return [unit];
    return esc.manageAll ? [] : mine.slice(0, 1);
  };
  const create = useAct(() => api.post<{ id: string }>("/sociedade/reunioes", { title: "Reunião de sócios", date: todayIso(), unitIds: newUnits() }), inv, (r) => set({ reuniao: r.id }), "Reunião criada");
  if (openId) return <MeetingEditor id={openId} onBack={() => set({ reuniao: null })} />;
  if (error) return <ErrorBox error={error} />;
  if (!data) return <Loading />;
  return (
    <Card title="Reuniões" actions={can("sociedade.gerenciar") && <button className="btn small primary" disabled={create.isPending} onClick={() => create.mutate()}>Nova reunião</button>}>
      {data.map((m) => (
        <div key={m.id} className="lrow" onClick={() => set({ reuniao: m.id })}>
          <Icon name="doc" />
          <div className="grow"><div className="tt">Nº {m.number} — {m.title}</div><div className="mm">{fmtDate(m.date)}</div></div>
          <UnitTags ids={m.unitIds} />
          <Badge tone={m.status === "realizada" ? "ok" : "info"}>{m.status}</Badge>
        </div>
      ))}
      {!data.length && <div className="muted small">Nenhuma reunião registrada.</div>}
    </Card>
  );
}

function MeetingEditor({ id, onBack }: { id: string; onBack: () => void }): JSX.Element {
  const { can, catalog } = useLookups();
  const qc = useQueryClient();
  const { data, error } = useQuery({ queryKey: ["soc-reuniao", id], queryFn: () => api.get<Meeting & { actions: Act[] }>(`/sociedade/reunioes/${id}`) });
  const edit = can("sociedade.gerenciar") && !data?.readOnly;
  const [f, setF] = useState<Meeting | null>(null);
  const [extra, setExtra] = useState("");
  useEffect(() => { if (data) setF(data); }, [data]);
  const save = useAct((patch?: Partial<Meeting>) => api.patch(`/sociedade/reunioes/${id}`, { ...pick(f!), ...(patch ?? {}) }), inv);
  const pull = useAct(async () => {
    await api.patch(`/sociedade/reunioes/${id}`, pick(f!));
    return api.post<{ added: number }>(`/sociedade/reunioes/${id}/trazer-pauta`);
  }, inv, (r) => toast.ok(r.added ? `${r.added} item(ns) trazido(s) para a pauta` : "Nada novo na coluna Pauta"), false);
  useEffect(() => { if (pull.isSuccess) void qc.invalidateQueries({ queryKey: ["soc-reuniao", id] }); }, [pull.isSuccess, qc, id]);
  if (error) return <ErrorBox error={error} />;
  if (!f || !data) return <Loading />;
  const users = catalog?.users ?? [];
  const isUser = (p: string) => users.some((u) => u.id === p);
  const set = (patch: Partial<Meeting>) => setF({ ...f, ...patch });
  const toggleUser = (uid: string) => set({ participants: f.participants.includes(uid) ? f.participants.filter((p) => p !== uid) : [...f.participants, uid] });

  return (
    <div className="stack gap-16">
      <div className="row">
        <button className="btn ghost small" onClick={onBack}>← Reuniões</button>
        <b>Ata nº {data.number}</b>
        <Badge tone={f.status === "realizada" ? "ok" : "info"}>{f.status}</Badge>
        <span className="grow" />
        <button className="btn small" onClick={() => void api.download(`/sociedade/reunioes/${id}/pdf`, `ata-${data.number}.pdf`, true)}>PDF da ata</button>
        {edit && f.status === "agendada" && <button className="btn small" onClick={() => save.mutate({ status: "realizada" })}>Marcar como realizada</button>}
        {edit && <button className="btn small primary" disabled={save.isPending} onClick={() => save.mutate(undefined)}>Salvar</button>}
      </div>
      <ErrorBox error={save.error ?? pull.error} />
      <Card>
        <FormGrid cols={3}>
          <Field label="Título" span={2}><input className="input" value={f.title} disabled={!edit} onChange={(e) => set({ title: e.target.value })} /></Field>
          <Field label="Data"><input type="date" className="input" value={f.date.slice(0, 10)} disabled={!edit} onChange={(e) => set({ date: e.target.value })} /></Field>
          <Field label="Unidade(s) da reunião" span={3} hint={data.readOnly ? "Reunião de outra unidade — só leitura para você." : "Reunião conjunta: marque todas. Quem é sócio de qualquer uma delas vê a reunião inteira."}>
            {edit ? <UnitsPicker value={f.unitIds} onChange={(v) => set({ unitIds: v })} /> : <div className="chips"><UnitTags ids={f.unitIds} /></div>}
          </Field>
          <Field label="Participantes" span={3}>
            <div className="chips">
              {users.map((u) => <button key={u.id} type="button" disabled={!edit} className={`chip ${f.participants.includes(u.id) ? "on" : ""}`} onClick={() => toggleUser(u.id)}>{u.displayName}</button>)}
              {f.participants.filter((p) => !isUser(p)).map((p) => <button key={p} type="button" disabled={!edit} className="chip on" onClick={() => set({ participants: f.participants.filter((x) => x !== p) })}>{p} ✕</button>)}
              {edit && <input className="input" style={{ width: 200 }} placeholder="+ convidado (Enter)" value={extra} onChange={(e) => setExtra(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && extra.trim()) { set({ participants: [...f.participants, extra.trim()] }); setExtra(""); } }} />}
            </div>
          </Field>
        </FormGrid>
      </Card>
      <div className="grid cols-2">
        <Card title="Pauta" actions={edit && <button className="btn small" disabled={pull.isPending} onClick={() => pull.mutate()}>Trazer da coluna Pauta</button>}>
          <textarea className="input" rows={10} value={f.agenda} disabled={!edit} onChange={(e) => set({ agenda: e.target.value })} placeholder="Um assunto por linha" />
        </Card>
        <Card title="Deliberações">
          <textarea className="input" rows={10} value={f.decisions} disabled={!edit} onChange={(e) => set({ decisions: e.target.value })} placeholder="O que foi decidido" />
        </Card>
      </div>
      <Card title="Discussões (ata)">
        <textarea className="input" rows={12} value={f.minutes} disabled={!edit} onChange={(e) => set({ minutes: e.target.value })} placeholder="Registro do que foi discutido" />
      </Card>
      <Card title={`Ações desta reunião (${data.actions.length})`}>
        <Board meetingId={id} meetingUnits={data.unitIds} />
      </Card>
    </div>
  );
}

function pick(m: Meeting): Partial<Meeting> {
  return { title: m.title, date: m.date.slice(0, 10), status: m.status, participants: m.participants, agenda: m.agenda, minutes: m.minutes, decisions: m.decisions, unitIds: m.unitIds };
}
